import { Dictionary, groupBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  AmlTemplate,
  Domanda,
  GetTasksResponseAmlAndFatcaCrsStateEnum,
  GetTasksResponseMifidStateEnum,
  Validator,
} from '../../../api/generated';
import {
  DatiFatca,
  DominioRequest,
  DominioRequestIdDominioEnum,
  ElencoDominioResponse,
  PraticaSelf,
  SurveyRisposte,
} from '../../../api/generated/dob';
import { QuestionnaireRequestAnswerRequest } from '../../../api/generated/prometeia';
import {
  commonErrorMessages,
  homeOnboardingSelfAmlFatcaCrsSurveyMessages,
} from '../../../messages';
import { GlobalActions } from '../../../model';
import appRoutes from '../../../routing/app-routes';
import {
  AmlFatcaCrsSurveyState,
  getAmlPfSurvey,
  getAmlPfSurveyResponse,
  getFatca,
  HomeCard,
  HomeState,
  postAml,
  postFatca,
  resetAmlSurvey,
  resetMifidSurvey,
  resetSurvey,
  retrieveOnboardingSelfPracticeData,
  SurveyState,
  updateAMLPartialAnswers,
  updateStatusQuestionnaires,
  UserState,
} from '../../../store';
import { getDominio } from '../../../store/features/dob/utility/utility-thunks';
import { DobUtilityState } from '../../../store/features/dob/utility/utility-types';
import {
  mapAmlResponseToPartial,
  mapPartialAmlToPostRequest,
} from '../../../utils';
import { useAppDispatch, useAppSelector } from '../../redux';
import { Dependency, fieldsDependencies } from './fields-validation';
import { useAppNavigation } from '../../../utils/navigation.utils';

export interface AmlFatcaCrsSurveyLogic {
  amlTemplate: AmlTemplate;
  partialAMLQuestionnaireAnswers: QuestionnaireRequestAnswerRequest[];
  isLoadingAMLResponse: boolean;
  isLoadingAMLSave: boolean;
  updateAMLPartialResponse: (props: UpdateAmlPartialResponseProp[]) => void;
  isResponseAmlError: boolean;
  datiFatca: DatiFatca;
  onDatiFatcaChange: (key: keyof DatiFatca, fatcaValue: any) => void;
  onFatcaSubmit: () => void;
  titolareSubSectionQuestions: Domanda[];
  attivitaSubSectionQuestions: Domanda[];
  pepSubSectionQuestions: Domanda[];
  financialSubSectionQuestions: Domanda[];
  privacySectionQuestions: Domanda[];
  getDominioResponse: ElencoDominioResponse;
  isFatcaLoaded: boolean;
  getAmlAnswerDetailsByQuestionId: (
    questionCode: string,
    responseArray?: boolean
  ) => string | string[];
  datiFatcaDraftConsistency: DatiFatcaConsistency;
  handleAmlSubmit: () => void;
  amlConsistency: AmlSurveyConsistency;
  isEnabled: (dependecies: Dependency[]) => boolean;
  getDependencyConfiguration: (dependencies: Dependency[]) => {
    enabled: boolean;
    forcedValue?: string;
  };
  deleteAnswerByQuestionId: (questionCode: string | undefined) => void;
  saveQuestionnaire: () => void;
  closePopupError: () => void;
  submitted: boolean;
  openPopupError: boolean;
  popupErrorMessage: string;
  isMifidModifyOpen: boolean;
  setIsMifidModifyOpen: (open: boolean) => void;
  modifiedQuestionsTexts: string[];
}

export interface ResidenzaFiscaleConsistency {
  codiceNazione: boolean;
  codiceFiscaleEstero: boolean;
}

export interface DatiFatcaConsistency {
  listaResidenzaFiscale: Array<ResidenzaFiscaleConsistency>;
  residenteUSA: boolean;
  tin: boolean;
}

export interface AmlQuestionConsistency {
  questionId: number;
  valid: boolean;
}

export interface AmlSurveyConsistency {
  consistency: AmlQuestionConsistency[];
}

export interface UpdateAmlPartialResponseProp {
  questionCode: string;
  value: string | string[];
}

export const useHomeOnboardingSelfAmlFatcaCrsSurveyLogic = (
  actions: GlobalActions
): AmlFatcaCrsSurveyLogic => {
  const dispatch = useAppDispatch();
  const { appNavigate } = useAppNavigation();
  const { showToast, closeToast } = actions;
  const { formatMessage } = useIntl();
  const {
    amlTemplate,
    partialAMLQuestionnaireAnswers,
    getAmlError,
    getAmlStatus,
    datiFatca,
    getFatcaStatus,
    getFatcaError,
    postFatcaError,
    postFatcaStatus,
    postAmlError,
    postAmlStatus,
    amlResponse,
  } = useAppSelector(
    (state) => state.features.amlSurvey as AmlFatcaCrsSurveyState
  );

  const updatedOnlyQuestion = useRef<UpdateAmlPartialResponseProp[]>([]);
  const { updateQuestionnairesStatus } = useAppSelector(
    (state) => state.features.mifidSurvey as SurveyState
  );

  const { getDominioResponse } = useAppSelector(
    (state) => state.features.dob.utility as DobUtilityState
  );

  const praticaSelf = useAppSelector(
    (state) => state.features.home.practiceData as HomeCard<PraticaSelf>
  );
  const { userCode, name, surname } = useAppSelector(
    (state) => state.data.user as UserState
  );

  const { toDoActivities, practiceData } = useAppSelector(
    (state) => state.features.home as HomeState
  );

  const [titolareSubSectionQuestions, setTitolareSubSectionQuestions] =
    useState([]);
  const [attivitaSubSectionQuestions, setAttivitaSubSectionQuestions] =
    useState([]);
  const [pepSubSectionQuestions, setPepSubSectionQuestions] = useState([]);
  const [financialSubSectionQuestions, setFinancialSubSectionQuestions] =
    useState([]);
  const [privacySectionQuestions, setPrivacySectionQuestions] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [openPopupError, setOpenPopupError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isMifidModifyOpen, setIsMifidModifyOpen] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState<string>();
  const [amlConsistency, setAmlConsistency] =
    useState<AmlSurveyConsistency>(undefined);
  const token: string = useAppSelector((state) => state.data?.user?.token);

  const codiceCliente = useMemo(() => {
    return praticaSelf?.value?.codiceCliente || '';
  }, [praticaSelf]);

  const codicePratica = useMemo(() => {
    return praticaSelf?.value?.codicePratica || '';
  }, [praticaSelf]);

  useEffect(() => {
    if (
      !partialAMLQuestionnaireAnswers?.length &&
      !amlResponse &&
      getAmlStatus !== 'loading' &&
      getAmlStatus !== 'succeeded' &&
      getAmlStatus !== 'failed' &&
      codiceCliente &&
      codicePratica
    ) {
      dispatch(
        getAmlPfSurveyResponse({
          idCustomer: codiceCliente || '',
          idIncarico: codicePratica || '',
        })
      );
    }
  }, [
    dispatch,
    partialAMLQuestionnaireAnswers,
    amlResponse,
    getAmlStatus,
    codiceCliente,
    codicePratica,
  ]);

  useEffect(() => {
    if (token) {
      dispatch(getAmlPfSurvey('AML_PF'));
      dispatch(retrieveOnboardingSelfPracticeData());
    }
  }, [dispatch, token]);

  /***** FATCA *****/
  const [isFatcaLoaded, setIsFatcaLoaded] = useState(false);
  const [datiFatcaDraft, updateDatiFatcaDraft] = useState<DatiFatca>(undefined);
  const [datiFatcaDraftConsistency, updateDatiFatcaDraftConsistency] =
    useState<DatiFatcaConsistency>(undefined);

  /* retrieve fatca */
  useEffect(() => {
    if (
      praticaSelf.requestStatus === 'succeeded' &&
      toDoActivities.requestStatus === 'succeeded'
    ) {
      if (
        toDoActivities.value.amlAndFatcaCrsState ===
        GetTasksResponseAmlAndFatcaCrsStateEnum.Completed
      ) {
        if (
          !datiFatca &&
          getFatcaStatus !== 'loading' &&
          getFatcaStatus !== 'succeeded' &&
          getFatcaStatus !== 'failed'
        ) {
          dispatch(
            getFatca({
              idCustomer: codiceCliente || '',
              idIncarico: codicePratica || '',
            })
          );
        }
        if (
          !amlResponse &&
          getAmlStatus !== 'loading' &&
          getAmlStatus !== 'succeeded' &&
          getAmlStatus !== 'failed'
        ) {
          dispatch(
            getAmlPfSurveyResponse({
              idCustomer: codiceCliente || '',
              idIncarico: codicePratica || '',
            })
          );
        }
      }
    }
  }, [
    praticaSelf,
    toDoActivities,
    dispatch,
    codiceCliente,
    codicePratica,
    datiFatca,
    getFatcaStatus,
    amlResponse,
    getAmlStatus,
  ]);
  useEffect(() => {
    if (
      getFatcaStatus === 'succeeded' &&
      !getFatcaError &&
      datiFatcaDraft === undefined
    ) {
      updateDatiFatcaDraft(datiFatca);
    }
  }, [getFatcaStatus, getFatcaError, datiFatca, datiFatcaDraft]);

  useEffect(() => {
    setIsFatcaLoaded(
      datiFatcaDraft !== undefined && getDominioResponse !== undefined
    );
  }, [datiFatcaDraft, getDominioResponse]);

  const baseFatca = useMemo(() => {
    return {
      idIncarico: +(codicePratica || ''),
      listaResidenzaFiscale: [],
      tin: null,
    };
  }, [codicePratica]);

  /* handle update fatca draft */
  const onDatiFatcaChange = useCallback(
    (key: keyof DatiFatca, fatcaValue: any) => {
      if (datiFatcaDraft) {
        if (key === 'residenteUSA' && fatcaValue) {
          updateDatiFatcaDraft({
            ...datiFatcaDraft,
            [key]: fatcaValue,
            listaResidenzaFiscale: [],
          });
        } else {
          updateDatiFatcaDraft({
            ...datiFatcaDraft,
            [key]: fatcaValue,
          });
        }
      } else
        updateDatiFatcaDraft({
          ...baseFatca,
          [key]: fatcaValue,
        });
      updateDatiFatcaDraftConsistency(undefined);
    },
    [baseFatca, datiFatcaDraft]
  );

  /* consistency check callbacks for fatca */
  const checkQuestionarioFatca = useCallback(() => {
    const check: DatiFatcaConsistency = {
      listaResidenzaFiscale: datiFatcaDraft?.listaResidenzaFiscale?.map((r) => {
        return {
          codiceNazione:
            datiFatcaDraft.residenteUSA === false &&
            (!!r.codiceNazione ||
              (!r.codiceFiscaleEstero &&
                datiFatcaDraft?.listaResidenzaFiscale?.length < 2)),
          codiceFiscaleEstero:
            datiFatcaDraft.residenteUSA === false &&
            ((!!r.codiceFiscaleEstero && r.codiceFiscaleEstero?.length <= 20) ||
              (!r.codiceNazione &&
                datiFatcaDraft?.listaResidenzaFiscale?.length < 2)),
        };
      }),
      residenteUSA: datiFatcaDraft?.residenteUSA !== undefined,
      tin:
        datiFatcaDraft?.residenteUSA === false ||
        (datiFatcaDraft?.residenteUSA && !!datiFatcaDraft?.tin),
    };
    return check;
  }, [datiFatcaDraft]);

  const onFatcaSubmit = useCallback(() => {
    setSubmitted(true);
    const questionnaireCheck = checkQuestionarioFatca();
    updateDatiFatcaDraftConsistency(questionnaireCheck);
    if (
      !questionnaireCheck.tin ||
      !questionnaireCheck.residenteUSA ||
      questionnaireCheck?.listaResidenzaFiscale?.some(
        (r) => !r.codiceFiscaleEstero || !r.codiceNazione
      )
    ) {
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorFieldAml),
        type: 'error',
      });
    } else {
      dispatch(
        postFatca({ body: datiFatcaDraft, idCustomer: codiceCliente || '' })
      );
    }
  }, [
    checkQuestionarioFatca,
    closeToast,
    datiFatcaDraft,
    dispatch,
    formatMessage,
    showToast,
    codiceCliente,
  ]);

  useEffect(() => {
    if (postFatcaError || postAmlError) {
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        type: 'error',
      });
      setSubmitted(false);
      return;
    }
  }, [closeToast, formatMessage, postAmlError, postFatcaError, showToast]);

  useEffect(() => {
    if (
      postFatcaStatus === 'succeeded' &&
      postAmlStatus === 'succeeded' &&
      submitted
    ) {
      setSubmitted(false);
      if (
        updatedOnlyQuestion.current.length > 0 &&
        toDoActivities.value?.mifidState ===
          GetTasksResponseMifidStateEnum.Completed
      ) {
        dispatch(
          updateStatusQuestionnaires({
            amlAndFactaCrsState:
              GetTasksResponseAmlAndFatcaCrsStateEnum.Completed,
            mifidState: GetTasksResponseMifidStateEnum.ToComplete,
          })
        );
      } else {
        const isMifidCompleted =
          toDoActivities.value?.mifidState ===
          GetTasksResponseMifidStateEnum.Completed;
        if (isMifidCompleted) {
          dispatch(
            updateStatusQuestionnaires({
              amlAndFactaCrsState:
                GetTasksResponseAmlAndFatcaCrsStateEnum.Completed,
              mifidState: practiceData.value.flagProfilaturaMifid
                ? GetTasksResponseMifidStateEnum.Completed
                : GetTasksResponseMifidStateEnum.NotFillable,
            })
          );
        } else {
          dispatch(
            updateStatusQuestionnaires({
              amlAndFactaCrsState:
                GetTasksResponseAmlAndFatcaCrsStateEnum.Completed,
              mifidState: practiceData.value.flagProfilaturaMifid
                ? GetTasksResponseMifidStateEnum.ToComplete
                : GetTasksResponseMifidStateEnum.NotFillable,
            })
          );
        }
      }
    }
  }, [dispatch, postAmlStatus, postFatcaStatus, practiceData, submitted]);

  useEffect(() => {
    if (updateQuestionnairesStatus === 'succeeded') {
      showToast({
        close: closeToast,
        title: formatMessage(
          homeOnboardingSelfAmlFatcaCrsSurveyMessages.finishedAmlTitle
        ),
        description: formatMessage(
          homeOnboardingSelfAmlFatcaCrsSurveyMessages.finishedAmlDesc,
          {
            customerName: name + ' ' + surname,
          }
        ),
        type: 'success',
      });
      dispatch(resetAmlSurvey());
      dispatch(resetSurvey());
      dispatch(resetMifidSurvey());
      appNavigate(appRoutes.Dashboard, { replace: true });
      return;
    }

    if (updateQuestionnairesStatus === 'failed') {
      setSubmitted(false);
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        type: 'error',
      });
    }
  }, [
    updateQuestionnairesStatus,
    appNavigate,
    showToast,
    closeToast,
    formatMessage,
    name,
    surname,
    dispatch,
    submitted,
  ]);

  /* handle get aml */
  const updateQuestionSections = useCallback(() => {
    if (amlTemplate) {
      const titSubSectionQuestions = [];
      const attSubSectionQuestions = [];
      const pepSectionQuestions = [];
      const finSubSectionQuestions = [];
      // todo: togliere ckeck dopo test che l'oggetto arrivi in tempo
      amlTemplate.sezioni.map((sezione) => {
        if (sezione.idSezione === 'ANTIRICICLAGGIO') {
          sezione.listaDomande.map((domanda) => {
            if (domanda.idDomanda >= 44 && domanda.idDomanda <= 50) {
              titSubSectionQuestions.push(domanda);
            }
            if (domanda.idDomanda >= 51 && domanda.idDomanda <= 53) {
              attSubSectionQuestions.push(domanda);
            }
            if (domanda.idDomanda >= 54 && domanda.idDomanda <= 66) {
              pepSectionQuestions.push(domanda);
            }
            if (domanda.idDomanda >= 272 && domanda.idDomanda <= 283) {
              finSubSectionQuestions.push(domanda);
            }
          });
        } else if (sezione.idSezione === 'PRIVACY') {
          setPrivacySectionQuestions(sezione.listaDomande);
        }
      });

      setTitolareSubSectionQuestions(titSubSectionQuestions);
      setAttivitaSubSectionQuestions(attSubSectionQuestions);
      setPepSubSectionQuestions(pepSectionQuestions);
      setFinancialSubSectionQuestions(finSubSectionQuestions);
    }
  }, [amlTemplate]);

  const getQuestionById = useCallback(
    (id: string) => {
      const questions = amlTemplate?.sezioni.flatMap(
        (section) => section.listaDomande
      );
      return questions?.find((item) => item.idDomanda === Number(id));
    },
    [amlTemplate]
  );

  const isAnswerSelected = useCallback(
    (validator: Validator) => {
      const getAnswers = (item: Validator) =>
        partialAMLQuestionnaireAnswers?.filter(
          (val) => Number(val.questionCode) === item.idDomandaDipendenza
        );
      const answers = getAnswers(validator);
      return answers?.some(
        (v) => v.value === validator.idRispostaDipendenza.toString()
      );
    },
    [partialAMLQuestionnaireAnswers]
  );

  const checkOrValidators = useCallback(
    (validators: any) =>
      validators.reduce(
        (acc, curr) => {
          const answer = isAnswerSelected(curr);
          if (answer) {
            return {
              isInvalid: true,
              questions: acc.questions.concat({
                id: curr.idDomandaDipendenza,
                isValid: false,
              }),
            };
          }

          return {
            isInvalid: acc.isInvalid,
            questions: acc.questions.concat({
              id: curr.idDomandaDipendenza,
              isValid: true,
            }),
          };
        },
        { isInvalid: false, questions: [] }
      ),
    [isAnswerSelected]
  );

  const checkAndValidators = useCallback(
    (validators: any) => validators.every(isAnswerSelected),
    [isAnswerSelected]
  );

  const checkAnswerValid = useCallback(
    (questionCode: string, value: string) => {
      const question = getQuestionById(questionCode);
      const validators = question?.validators.filter(
        (item) => item.idRisposta === Number(value)
      );
      let validatorsByTypes: Dictionary<Validator[]>;
      let questionsOr: { id: number; isValid: boolean }[];
      let questions: {
        id: number;
        isValid: boolean;
      }[] = [];

      let isInvalid = false;
      if (validators?.length > 0) {
        validatorsByTypes = groupBy(validators, 'tipoDipendenza');
        isInvalid = Object.entries(validatorsByTypes).every(
          ([key, validator]) => {
            if (key === 'validate_ko_and') {
              return checkAndValidators(validator);
            }

            const p = checkOrValidators(validator);
            questionsOr = p.questions;
            return p.isInvalid;
          }
        );

        questions = validatorsByTypes?.['validate_ko_and']?.map((val) => ({
          id: val.idDomandaDipendenza,
          isValid: !isInvalid,
        }));

        if (questionsOr) {
          questions = questions ? questions.concat(questionsOr) : questionsOr;
        }
      } else if (question?.validators.length > 0) {
        const otherAnswerValidators = question.validators.filter(
          (item) => item.idRisposta !== Number(value)
        );
        const ids = otherAnswerValidators.filter((val) =>
          partialAMLQuestionnaireAnswers?.some(
            (v) =>
              Number(v.questionCode) === val.idDomandaDipendenza &&
              Number(v.value) === val.idRispostaDipendenza
          )
        );
        questions = ids.map((val) => ({
          isValid: checkAnswerValid(
            val.idDomandaDipendenza.toString(),
            val.idRispostaDipendenza.toString()
          ).isValid,
          id: val.idDomandaDipendenza,
        }));
      }

      const others = questions
        ?.filter((val) => !val?.isValid)
        .map(({ id }) => `"${getQuestionById(id.toString())?.testoDomanda}"`)
        ?.join(',');
      return {
        isValid: !isInvalid,
        message: isInvalid
          ? formatMessage(
              homeOnboardingSelfAmlFatcaCrsSurveyMessages.questionErrorMessage,
              { first: question.testoDomanda, others }
            )
          : '',
        questions: (questions ?? []).concat([
          {
            id: Number(questionCode),
            isValid: !isInvalid,
          },
        ]),
      };
    },
    [
      partialAMLQuestionnaireAnswers,
      getQuestionById,
      checkOrValidators,
      checkAndValidators,
    ]
  );

  const checkAnswer = useCallback(
    (questionCode: string, value: string) => {
      const clone: AmlSurveyConsistency = amlConsistency
        ? { ...amlConsistency }
        : { consistency: [] };
      const response = checkAnswerValid(questionCode, value);
      response.questions?.forEach((q) => {
        const questionConsistency: AmlQuestionConsistency =
          clone.consistency.find(
            (item: AmlQuestionConsistency) => item.questionId === q.id
          );
        if (!questionConsistency) {
          clone.consistency.push({ questionId: q.id, valid: q.isValid });
        } else {
          questionConsistency.valid = q.isValid;
        }
      });

      setAmlConsistency(clone);
      if (!response.isValid) {
        setOpenPopupError(true);
        setPopupErrorMessage(response.message);
      }
    },
    [checkAnswerValid, amlConsistency]
  );

  useEffect(() => {
    if (
      !partialAMLQuestionnaireAnswers ||
      partialAMLQuestionnaireAnswers.length === 0 ||
      !isDirty
    ) {
      setAmlConsistency(undefined);
      return;
    }
    const lastAnswer = partialAMLQuestionnaireAnswers.slice(-1)[0];
    const answerToCheck = partialAMLQuestionnaireAnswers
      .filter((val) => val.questionCode === lastAnswer.questionCode)
      .find((answer) => {
        const question = getQuestionById(answer.questionCode);
        const validators = question?.validators.filter(
          (item) => item.idRisposta === Number(answer.value)
        );
        return validators?.length > 0;
      });
    if (answerToCheck) {
      checkAnswer(answerToCheck.questionCode, answerToCheck.value);
    } else if (lastAnswer) {
      checkAnswer(lastAnswer.questionCode, lastAnswer.value);
    }
  }, [partialAMLQuestionnaireAnswers]);

  const updateAMLPartialResponse = useCallback(
    (props: UpdateAmlPartialResponseProp[]) => {
      let response = [];
      const questionToCheck = [
        '272',
        '273',
        '274,',
        '275',
        '276',
        '277',
        '278',
        '279',
        '280',
        '281',
        '282',
        '283',
      ];

      for (const p of props) {
        const { questionCode, value } = p;
        if (questionToCheck.includes(questionCode)) {
          const questionIndex = updatedOnlyQuestion.current.findIndex(
            (f) => f.questionCode === questionCode
          );
          const savedResponseAmlQuestion = amlResponse?.listaDomande?.find(
            (f) => f.idDomanda === Number(questionCode)
          );

          if (questionIndex !== -1) {
            if (
              savedResponseAmlQuestion?.listaRisposte.find(
                (f) => f.idRisposta === Number(value)
              )
            ) {
              updatedOnlyQuestion.current.splice(questionIndex, 1);
            } else {
              updatedOnlyQuestion.current[questionIndex] = p;
            }
          } else {
            updatedOnlyQuestion.current.push(p);
          }
        }

        if (questionCode) {
          const answers = [];
          if (Array.isArray(value)) {
            value.forEach((a) => {
              const answer: QuestionnaireRequestAnswerRequest = {
                questionCode,
                questionTableId: 0,
                value: a,
              };
              answers.push(answer);
            });
          } else {
            const answer: QuestionnaireRequestAnswerRequest = {
              questionCode,
              questionTableId: 0,
              value,
            };
            answers.push(answer);
          }
          response = answers;
        }
      }
      dispatch(updateAMLPartialAnswers(response));
      setIsDirty(true);
    },
    [dispatch, amlResponse]
  );

  useEffect(() => {
    if (getAmlError && getAmlStatus !== 'succeeded') {
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        type: 'error',
      });
    }
  }, [closeToast, formatMessage, getAmlError, showToast]);

  /* retrieve domains and update questions sections aml */
  const domains = useMemo(() => {
    if (amlTemplate) {
      const allDomains = amlTemplate?.sezioni
        ?.flatMap((s) =>
          s?.listaDomande
            ?.filter((d) => d?.tipoDominio)
            .map((d) => d.tipoDominio)
        )
        .concat([DominioRequestIdDominioEnum.NazioneResidenzaFiscale]);
      return [...new Set(allDomains)];
    }
    return [];
  }, [amlTemplate]);

  useEffect(() => {
    if (getAmlStatus === 'succeeded' && !getAmlError) {
      updateQuestionSections();

      dispatch(
        getDominio({
          listaDomini: domains.map((d) => {
            return { idDominio: d } as DominioRequest;
          }),
        })
      );
    }
  }, [
    getAmlStatus,
    getAmlError,
    updateQuestionSections,
    amlTemplate,
    dispatch,
    domains,
  ]);

  const getAmlAnswerDetailsByQuestionId = useCallback(
    (questionCode: string, responseArray?: boolean) => {
      if (questionCode !== undefined) {
        if (responseArray) {
          return partialAMLQuestionnaireAnswers
            ?.filter((v) => v.questionCode === '' + questionCode)
            .map((v) => v.value);
        }
        return partialAMLQuestionnaireAnswers?.find(
          (v) => v.questionCode === '' + questionCode
        )?.value;
      }
      return '';
    },
    [partialAMLQuestionnaireAnswers]
  );

  /*** AML save and consistency ***/

  const isEnabled = useCallback(
    (dependencies: Dependency[]) => {
      let foundFirst: boolean;
      const foundSecond: boolean[] = [];
      let mandatoryDependencies = 0;
      dependencies.forEach((dependency) => {
        mandatoryDependencies++;
        foundFirst = !dependency.enable_answer;
        dependency.question_dependency_ids.forEach((questionDependencyId) => {
          if (getAmlAnswerDetailsByQuestionId(questionDependencyId)) {
            const answerId = getAmlAnswerDetailsByQuestionId(
              questionDependencyId
            ) as string;
            if (dependency.valid_response_ids.includes(answerId)) {
              foundFirst = dependency.enable_answer;
            }
          }
        });
        foundSecond.push(foundFirst);
      });
      return (
        foundSecond.length == mandatoryDependencies &&
        foundSecond.every((found) => found == true)
      );
    },
    [getAmlAnswerDetailsByQuestionId]
  );

  const getDependencyConfiguration = useCallback(
    (dependencies: Dependency[]) => {
      let foundFirst: boolean;
      const foundSecond: boolean[] = [];
      let mandatoryDependencies = 0;
      let forcedValue: string | undefined;
      dependencies.forEach((dependency) => {
        mandatoryDependencies++;
        foundFirst = !dependency.enable_answer;
        let matchFound = false;
        dependency.question_dependency_ids.forEach((questionDependencyId) => {
          if (getAmlAnswerDetailsByQuestionId(questionDependencyId)) {
            const answerId = getAmlAnswerDetailsByQuestionId(
              questionDependencyId
            ) as string;
            if (dependency.valid_response_ids.includes(answerId)) {
              foundFirst = dependency.enable_answer;
              matchFound = true;
            }
          }
        });
        if (
          matchFound &&
          dependency.enable_answer === false &&
          dependency.forced_value
        ) {
          forcedValue = dependency.forced_value;
        }
        foundSecond.push(foundFirst);
      });
      return {
        enabled:
          foundSecond.length == mandatoryDependencies &&
          foundSecond.every((found) => found == true),
        forcedValue,
      };
    },
    [getAmlAnswerDetailsByQuestionId]
  );

  const getAmlPostRequest = useCallback(() => {
    return {
      idCliente: userCode || '',
      idIncarico: praticaSelf.value?.codicePratica || '',
      listaDomande:
        mapPartialAmlToPostRequest(
          amlTemplate,
          partialAMLQuestionnaireAnswers
        ) || [],
    } as SurveyRisposte;
  }, [userCode, amlTemplate, partialAMLQuestionnaireAnswers, praticaSelf]);

  const performAmlConsistencyCheck = useCallback(() => {
    if (!amlTemplate)
      return { consistency: { consistency: [] }, isConsistent: false };
    const questionIds: number[] = amlTemplate.sezioni.flatMap((s) =>
      s.listaDomande.flatMap((d) =>
        d.strutturaInternaDomande ? [] : d.idDomanda
      )
    );

    /* first check: every question must be answered */
    let c: AmlSurveyConsistency = {
      consistency: questionIds.map((id) => {
        const response = partialAMLQuestionnaireAnswers.find(
          (a) => a.questionCode === '' + id
        );

        return {
          questionId: id,
          valid: [68, 69, 48].includes(id)
            ? true
            : response !== undefined &&
              checkAnswerValid(id.toString(), response.value).isValid,
        } as AmlQuestionConsistency;
      }),
    };

    Object.keys(fieldsDependencies).forEach((key) => {
      const targetQuestionId = fieldsDependencies[key].id;
      const isQuestionEnabled =
        targetQuestionId === 48
          ? false
          : isEnabled(fieldsDependencies[key].dependencies);
      const questionResponse =
        getAmlAnswerDetailsByQuestionId(targetQuestionId);

      c = {
        consistency: c.consistency
          .filter((q) => q.questionId !== targetQuestionId)
          .concat([
            {
              questionId: targetQuestionId,
              valid: isQuestionEnabled
                ? questionResponse !== undefined &&
                  checkAnswerValid(targetQuestionId, questionResponse as string)
                    .isValid
                : true,
            },
          ]),
      };
    });

    /* third check: pep questions */
    const pepResponse = getAmlAnswerDetailsByQuestionId('54') as string;
    if (['63', '64'].includes(pepResponse)) {
      // exclude from check
      // pepFamilyMemberQuestion & incomeBracketQuestion
      // & corporateInvestmentsQuestion & judicialProceedingsQuestion
      c = {
        consistency: c.consistency
          .filter((q) => ![62].includes(q.questionId))
          .concat(
            [62].map((i) => {
              return {
                questionId: i,
                valid: true,
              };
            })
          ),
      };
    }
    if (['65'].includes(pepResponse)) {
      // exclude from check positionQuestion & startDatePositionQuestion
      // & sourceOfIncomeQuestion & pepAsQuestion & incomeBracketQuestion
      // & corporateInvestmentsQuestion & judicialProceedingsQuestion
      c = {
        consistency: c.consistency
          .filter((q) => ![55, 56, 57, 58].includes(q.questionId))
          .concat(
            [55, 56, 57, 58].map((i) => {
              return {
                questionId: i,
                valid: true,
              };
            })
          ),
      };
    }
    if (!['63', '64', '65'].includes(pepResponse)) {
      c = {
        consistency: c.consistency
          .filter(
            (q) => ![55, 56, 57, 58, 59, 60, 61, 62].includes(q.questionId)
          )
          .concat(
            [55, 56, 57, 58, 59, 60, 61, 62].map((i) => {
              return {
                questionId: i,
                valid: true,
              };
            })
          ),
      };
    }

    //fourth chek: professionResponse
    const professionResponse = getAmlAnswerDetailsByQuestionId('49') as string;
    if (['002', '003', '110'].includes(professionResponse)) {
      c = {
        consistency: c.consistency
          .filter((q) => ![47].includes(q.questionId))
          .concat(
            [47].map((i) => {
              return {
                questionId: i,
                valid: true,
              };
            })
          ),
      };
    }

    const isConsistent = !c.consistency?.some((q) => !q.valid);
    return { consistency: c, isConsistent };
  }, [
    amlTemplate,
    getAmlAnswerDetailsByQuestionId,
    isEnabled,
    partialAMLQuestionnaireAnswers,
  ]);

  const showModalModifiedMifidQuestion = useCallback(() => {
    setIsMifidModifyOpen(true);
  }, []);

  const modifiedQuestionsTexts = useMemo(() => {
    return updatedOnlyQuestion.current
      .map((q) => getQuestionById(q.questionCode)?.testoDomanda)
      .filter((text): text is string => !!text);
  }, [isMifidModifyOpen, amlTemplate, getQuestionById]);

  const handleAmlSubmit = useCallback(() => {
    const { consistency, isConsistent } = performAmlConsistencyCheck();

    setAmlConsistency(consistency);

    if (isConsistent) {
      dispatch(postAml(getAmlPostRequest()));
    } else {
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorFieldAml),
        type: 'error',
      });
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    getAmlPostRequest,
    performAmlConsistencyCheck,
    showToast,
  ]);

  const deleteAnswerByQuestionId = useCallback(
    (questionCode: string) => {
      dispatch(updateAMLPartialAnswers([{ questionCode, value: '' }]));
      setIsDirty(true);
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(updateAMLPartialAnswers(mapAmlResponseToPartial(amlResponse)));
  }, [amlResponse]);

  const saveQuestionnaire = useCallback(() => {
    if (
      updatedOnlyQuestion.current.length > 0 &&
      toDoActivities.value?.mifidState ===
        GetTasksResponseMifidStateEnum.Completed
    ) {
      showModalModifiedMifidQuestion();
    } else {
      handleAmlSubmit();
      onFatcaSubmit();
    }
  }, [
    handleAmlSubmit,
    onFatcaSubmit,
    showModalModifiedMifidQuestion,
    toDoActivities,
  ]);

  const closePopupError = useCallback(() => {
    setOpenPopupError(false);
    setPopupErrorMessage(undefined);
  }, []);

  // AZCRO-7934
  useEffect(() => {
    // PEP question
    const answer54 = getAmlAnswerDetailsByQuestionId('54');
    switch (answer54) {
      case '62':
        // set to NO PEP questions when question 54 is "62" (NO)
        updateAMLPartialResponse([{ questionCode: '63', value: '84' }]);
        updateAMLPartialResponse([{ questionCode: '64', value: '84' }]);
        updateAMLPartialResponse([{ questionCode: '65', value: '84' }]);
        updateAMLPartialResponse([{ questionCode: '66', value: '84' }]);
        break;
      default:
        break;
    }
  }, [getAmlAnswerDetailsByQuestionId('54')]);

  return {
    amlTemplate,
    partialAMLQuestionnaireAnswers,
    isLoadingAMLResponse: getAmlStatus === 'loading',
    isLoadingAMLSave: [postAmlStatus, postFatcaStatus].includes('loading'),
    updateAMLPartialResponse,
    isResponseAmlError: getAmlError,
    datiFatca: datiFatcaDraft,
    onDatiFatcaChange,
    onFatcaSubmit,
    titolareSubSectionQuestions,
    attivitaSubSectionQuestions,
    pepSubSectionQuestions,
    financialSubSectionQuestions,
    privacySectionQuestions,
    getDominioResponse,
    isFatcaLoaded,
    getAmlAnswerDetailsByQuestionId,
    datiFatcaDraftConsistency,
    handleAmlSubmit,
    amlConsistency,
    isEnabled,
    getDependencyConfiguration,
    deleteAnswerByQuestionId,
    saveQuestionnaire,
    submitted,
    closePopupError,
    openPopupError,
    popupErrorMessage,
    isMifidModifyOpen,
    setIsMifidModifyOpen,
    modifiedQuestionsTexts,
  };
};
